import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Clock3,
  Fuel,
  Plus,
  ReceiptText,
} from "lucide-react";
import { Badge, Button, Card, EmptyState, MetricCard, PageHeader } from "../components/ui";
import {
  activityCost,
  budgetSnapshot,
  compactCurrency,
  currency,
  monthIso,
  shortDate,
  todayIso,
} from "../lib/calculations";
import type { AppData } from "../types";
import type { AppView } from "../components/AppShell";

export function DashboardPage({
  data,
  onView,
  onNewWork,
}: {
  data: AppData;
  onView(view: AppView): void;
  onNewWork(): void;
}) {
  const [selectedMonth, setSelectedMonth] = useState(monthIso());
  const snapshot = useMemo(
    () => budgetSnapshot(data.clients, data.monthlyCosts, data.tools, data.assumptions),
    [data],
  );
  const monthActivities = data.activities.filter((item) =>
    item.work_date.startsWith(selectedMonth),
  );
  const completed = monthActivities.filter((item) => item.status === "Completed");
  const hours = completed.reduce((sum, item) => sum + item.duration_minutes, 0) / 60;
  const received = monthActivities.reduce((sum, item) => sum + item.amount_received, 0);
  const costs = completed.reduce(
    (sum, item) => sum + activityCost(item, data.assumptions),
    0,
  );
  const fuelLitres = completed.reduce((sum, item) => sum + item.petrol_litres, 0);
  const today = todayIso();
  const todaysWork = data.activities.filter((item) => item.work_date === today);
  const recent = data.activities.slice(0, 5);
  const unconfirmed = data.clients.filter((item) => !item.confirmed).length;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Daily operations"
        title="Work dashboard"
        description="See what is happening today and how the month is tracking."
        actions={
          <>
            <label className="month-picker">
              <span className="sr-only">Dashboard month</span>
              <input type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
            </label>
            <Button onClick={onNewWork}><Plus size={18} /> Log work</Button>
          </>
        }
      />

      <section className="metrics-grid" aria-label="Monthly activity summary">
        <MetricCard label="Completed visits" value={String(completed.length)} detail={`${monthActivities.length} total entries`} tone="dark" />
        <MetricCard label="Hours worked" value={hours.toFixed(1)} detail="Completed work" />
        <MetricCard label="Money received" value={compactCurrency.format(received)} detail="Recorded payments" tone="brand" />
        <MetricCard label="Direct job costs" value={compactCurrency.format(costs)} detail={`${fuelLitres.toFixed(1)} L petrol used`} tone={costs > received && received > 0 ? "danger" : "default"} />
      </section>

      <div className="dashboard-grid">
        <Card className="dashboard-today">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{shortDate.format(new Date(`${today}T00:00:00`))}</p>
              <h2>Today’s work</h2>
            </div>
            <Button variant="ghost" onClick={() => onView("work")}>View log <ArrowRight size={17} /></Button>
          </div>
          {todaysWork.length === 0 ? (
            <EmptyState title="Nothing logged today" description="Add a visit before or after the job so the record stays complete." action={<Button variant="secondary" onClick={onNewWork}>Log today’s work</Button>} />
          ) : (
            <div className="work-list">
              {todaysWork.map((item) => {
                const client = data.clients.find((row) => row.id === item.client_id);
                return (
                  <div className="work-row" key={item.id}>
                    <div className="work-time"><Clock3 size={17} />{item.start_time?.slice(0, 5) || "Any time"}</div>
                    <div className="work-main"><strong>{client?.client_name || "No client selected"}</strong><span>{item.activity_type}</span></div>
                    <Badge tone={item.status === "Completed" ? "success" : item.status === "Cancelled" ? "danger" : "warning"}>{item.status}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="budget-glance">
          <div className="section-heading"><div><p className="eyebrow">Workbook plan</p><h2>Monthly budget</h2></div><button className="round-link" onClick={() => onView("budget")} aria-label="Open monthly budget"><ArrowRight size={18} /></button></div>
          <div className="budget-number"><span>Average recurring income</span><strong>{currency.format(snapshot.averageRecurringIncome)}</strong></div>
          <dl className="budget-mini-list">
            <div><dt>Cash operating costs</dt><dd>{currency.format(snapshot.cashOperatingCosts)}</dd></div>
            <div><dt>Contingency</dt><dd>{currency.format(snapshot.operatingContingency)}</dd></div>
            <div><dt>Equipment reserve</dt><dd>{currency.format(snapshot.equipmentReserve)}</dd></div>
            <div className="budget-mini-total"><dt>Spendable cash</dt><dd>{currency.format(snapshot.spendableMonthlyCash)}</dd></div>
          </dl>
        </Card>

        <Card className="recent-card">
          <div className="section-heading"><div><p className="eyebrow">Latest updates</p><h2>Recent entries</h2></div></div>
          {recent.length === 0 ? (
            <p className="muted">Your first saved work entry will appear here.</p>
          ) : (
            <div className="recent-list">
              {recent.map((item) => {
                const client = data.clients.find((row) => row.id === item.client_id);
                return (
                  <div key={item.id} className="recent-item">
                    <span className="recent-icon">{item.petrol_litres > 0 ? <Fuel size={18} /> : <ReceiptText size={18} />}</span>
                    <div><strong>{item.activity_type}</strong><span>{client?.client_name || "General work"} · {shortDate.format(new Date(`${item.work_date}T00:00:00`))}</span></div>
                    <strong>{item.amount_received > 0 ? compactCurrency.format(item.amount_received) : "—"}</strong>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="attention-card">
          <div className="section-heading"><div><p className="eyebrow">Needs attention</p><h2>Planning checks</h2></div><AlertTriangle size={21} /></div>
          <button className="attention-row" onClick={() => onView("clients")}>
            <span><UsersMark count={unconfirmed} /><span><strong>Client details</strong><small>{unconfirmed} name or frequency confirmations open</small></span></span><ArrowRight size={17} />
          </button>
          <button className="attention-row" onClick={() => onView("tools")}>
            <span><Banknote size={19} /><span><strong>Starter tools</strong><small>{compactCurrency.format(snapshot.starterToolsToBuy)} still planned</small></span></span><ArrowRight size={17} />
          </button>
        </Card>
      </div>
    </div>
  );
}

function UsersMark({ count }: { count: number }) {
  return <span className="count-mark" aria-label={`${count} open`}>{count}</span>;
}
